import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { useHistory } from 'react-router';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { AppState } from '../store';
import { Path } from '../constants/path';
import * as propertySelectors from '../modules/property/selectors';
import * as propertyActions from '../modules/property/actions';
import * as indexActions from '../modules/index/actions';
import * as indexOperations from '../modules/index/operations';
import * as invitationOperations from '../modules/invitation/operations';
import { isContractorOnly } from '../modules/invitation';
import NotFoundScreen from '../screens/identification/errorScreen/NotFoundScreen';
import CanceledScreen from '../screens/identification/errorScreen/CanceledScreen';
import LockedScreen from '../screens/identification/errorScreen/LockedScreen';
import ServerErrorScreen from '../screens/identification/errorScreen/ServerErrorScreen';
import LoadingScreen from '../screens/identification/LoadingScreen';
import { hasSingleApplicationIdentifier } from '../modules/property/types';

interface Props {
  children: React.ReactNode;
}

export const IdentificationGuard: React.FC<Props> = ({ children }) => {
  const history = useHistory();
  const dispatch = useDispatch<ThunkDispatch<AppState, {}, AnyAction>>();

  const property = useSelector((state: AppState) =>
    propertySelectors.getProperty(state)
  );
  const invitationStatus = useSelector(
    (state: AppState) => state.invitation.status
  );

  const params = new URLSearchParams(history.location.search);
  const organizationId = params.get('o') || '';
  const contractInfoId = params.get('contract_info_id') || '';
  const usageInfoId = params.get('usage_info_id') || '';
  const peopleId = params.get('people_id') || '';
  const hasQueryParams =
    organizationId !== '' &&
    hasSingleApplicationIdentifier(contractInfoId, usageInfoId) &&
    peopleId !== '';

  useEffect(() => {
    if (!hasQueryParams || invitationStatus !== 'not_set') {
      return;
    }
    dispatch(propertyActions.setOrganizationId(organizationId));
    dispatch(propertyActions.setContractInfoId(contractInfoId));
    dispatch(propertyActions.setUsageInfoId(usageInfoId));
    dispatch(propertyActions.setPeopleId(peopleId));
    dispatch(indexActions.setQueryParams());
    dispatch(indexOperations.saveQueryParamsToCookie());
    dispatch(
      invitationOperations.verifyInvitation(
        organizationId,
        contractInfoId,
        usageInfoId,
        peopleId
      )
    );
  }, [
    dispatch,
    hasQueryParams,
    invitationStatus,
    organizationId,
    contractInfoId,
    usageInfoId,
    peopleId
  ]);

  // クエリが揃っていない申込みURLは、 404 とする
  if (!hasQueryParams) {
    return <NotFoundScreen />;
  }

  // 招待検証API の応答待ち。結果が出るまで本人確認画面は出さない
  if (invitationStatus === 'not_set' || invitationStatus === 'checking') {
    return <LoadingScreen />;
  }

  switch (invitationStatus) {
    case 'not_found':
    case 'invalid_request':
      return <NotFoundScreen />;
    case 'already_applied':
      return <Redirect to={Path.home} />;
    case 'canceled':
      return <CanceledScreen />;
    case 'locked':
      return <LockedScreen />;
    case 'internal':
      return <ServerErrorScreen />;
    default:
      break;
  }

  // 契約者用は本人確認をしないので申込情報入力へ直行する
  if (isContractorOnly(property.applicantType)) {
    return <Redirect to={Path.application.url} />;
  }

  return <>{children}</>;
};
